/* eslint-disable no-unused-vars */
import * as d3 from 'd3';
import _ from 'lodash';
import { swoopyDrag } from '../swoopy-drag';
import { wordwrap } from 'd3-jetpack';
import { intcomma, apmonth, apdate } from 'journalize';
import * as moment from 'moment';
import data from '../../../data/data.json';

export function customDateFormat(d) {
  let year = moment(d).format('YYYY');
  return `${year}`;
}

export function customMobileDateFormat(d, i) {
  let month = moment(d).format('MMM'),
    year = moment(d).format('YYYY');

  if (i % 2 == 0) return `${year}`;
  else return '';
}

// function for unemployment rate chart
function render() {
  // a reference to the default graphic container, change if needed
  const container = d3.select('#unemployment-rate-chart');

  // a helper function to grab the container's width
  const getFrameWidth = () => container.node().offsetWidth;

  // a helper function to clear the container of its contents
  const clearContainer = () => container.html('');

  clearContainer();

  //set margins
  let margin = {
    top: 20,
    left: 40,
    bottom: 40,
  };

  let breakPoint = 500;

  // grab the data we need
  let rateData = data.unemployment_rate_historical;

  // changing to moment date+time objects
  rateData.forEach(d => (d.date = moment(d.date, 'YYYY MMM')));

  // grabbing totals and ranges
  let dateRange = d3.extent(rateData, d => d.date),
    totalsMax = d3.max(rateData, d => d.value);

  let frameWidth = getFrameWidth(),
    frameHeight;

  if (frameWidth > breakPoint) {
    frameHeight = 400;
    margin.right = 80;
  } else {
    frameHeight = 350;
    margin.right = 70;
  }

  let chartWidth = frameWidth - margin.left - margin.right,
    chartHeight = frameHeight - margin.bottom;

  // set scale domains
  let x = d3.scaleTime().domain(dateRange);
  let y = d3
    .scaleLinear()
    .domain([0, totalsMax])
    .nice();

  const dates = {
    begin: dateRange[0],
    end: dateRange[1],
    pandemicRecessionBegin: moment('2020-03-01'),
    pandemicRecessionEnd: dateRange[1],
    greatRecessionBegin: moment('2007-12-01'),
    greatRecessionEnd: moment('2009-06-01'),
  };

  const scaleNum = {
    recession: 8.3,
    recessionLabel: 8.55,
    intro: 9.0,
  };

  // append svg
  let svg = container.append('svg'),
    g = svg.append('g').attr('class', 'unemployment-rate-line-chart');

  let xAxis = g.append('g').attr('class', 'axis axis--x'),
    yAxis = g.append('g').attr('class', 'axis axis--y');

  x.range([0, chartWidth]);
  y.range([chartHeight, 0]);

  drawEventArea(
    dates.greatRecessionBegin,
    dates.greatRecessionEnd,
    'Great Recession',
    frameWidth > breakPoint
      ? dates.greatRecessionBegin.clone().add(2, 'months')
      : dates.greatRecessionEnd.clone().add(1.5, 'months'),
    'start',
    0.9
  );

  drawEventArea(
    dates.pandemicRecessionBegin,
    dates.pandemicRecessionEnd,
    'Pandemic',
    dates.pandemicRecessionEnd.clone().add(1.5, 'months'),
    'start',
    0.3
  );

  // draw the rectangle behind the chart data to highlight the April shutdown
  let path = g
    .append('path')
    .data([rateData])
    .attr('class', 'line')
    .attr('fill', 'none')
    .attr('stroke', '#529aad')
    .attr('stroke-width', 2);

  // label the april drop
  let recordLabels = g.append('g').attr('class', 'record-label-elements');

  let records = [
    {
      date: moment('2019 May', 'YYYY MMM'),
      value: 3.4,
      text: 'record low',
      translate: '9, 20',
      anchor: 'end',
    },
    {
      date: moment('2020 April', 'YYYY MMM'),
      value: 12.9,
      text: 'record high',
      translate: '10, -11',
      anchor: 'start',
    },
  ];

  let recordElements = recordLabels
    .selectAll('g')
    .data(records)
    .enter()
    .append('g');

  recordElements
    .append('circle')
    .attr('fill', '#529aad')
    .attr('r', 4)
    .attr('class', 'label-point');

  let recordText = recordElements.append('g').attr('class', 'label-text');

  recordText
    .append('text')
    .text(d => `${d.value}%`)
    .attr('font-weight', 700);

  // label the end point
  let labels = g.append('g').attr('class', 'labeled-elements');

  let labelElements = labels
    .selectAll('g')
    .data(getLabeledData(rateData, []))
    .enter()
    .append('g')
    .attr('class', 'label-element');

  labelElements
    .append('circle')
    .attr('fill', '#529aad')
    .attr('r', 4)
    .attr('class', 'label-point');

  let labelText = labelElements.append('g').attr('class', 'label-text');

  labelText
    .append('text')
    .text(d => `${d.value}%`)
    .attr('font-weight', 700);

  // Add date annotations
  let dateAnno = labelText.append('text');
  let recordAnno = recordText.append('text');

  svg.attr('width', frameWidth).attr('height', frameHeight);
  g.attr('width', chartWidth).attr(
    'transform',
    `translate(${margin.left},${margin.top})`
  );

  xAxis
    .call(
      d3
        .axisBottom(x)
        .ticks(5)
        .tickFormat(
          frameWidth < breakPoint ? customMobileDateFormat : customDateFormat
        )
    )
    .attr('transform', `translate(0,${frameHeight - margin.bottom})`);

  yAxis.call(
    d3
      .axisLeft(y)
      .ticks(5)
      .tickPadding(10)
      .tickSize(-chartWidth)
      .tickFormat(d => `${d}%`)
  );

  // Add rate lines
  let line = d3
    .line()
    .x(d => x(d.date))
    .y(d => y(d.value));

  path.attr('d', line);

  labelElements
    .attr('transform', d => `translate(${x(d.date)},${y(d.value)})`)
    .attr('font-size', frameWidth > breakPoint ? '13px' : '12px');

  recordElements
    .attr('transform', d => `translate(${x(d.date)},${y(d.value)})`)
    .attr('font-size', frameWidth > breakPoint ? '13px' : '12px');

  recordText
    .attr('transform', (d, i) => {
      if (i === 0 && frameWidth < breakPoint) {
        return 'translate(9, 15)';
      } else return `translate(${d.translate})`;
    })
    .attr('text-anchor', d => d.anchor);

  labelText
    .attr('transform', (d, i) => {
      if (i === 0 && frameWidth < breakPoint) {
        return 'translate(9, 0)';
      } else return 'translate(9, 0)';
    })
    .attr('text-anchor', 'start');

  dateAnno.attr('y', frameWidth > breakPoint ? 17 : 13);

  let apMonths = new Map([
    [1, 'Jan.'],
    [2, 'Feb.'],
    [3, 'March'],
    [4, 'April'],
    [5, 'May'],
    [6, 'June'],
    [7, 'July'],
    [8, 'Aug.'],
    [9, 'Sept.'],
    [10, 'Oct.'],
    [11, 'Nov.'],
    [12, 'Dec.'],
  ]);

  dateAnno.each(function(d) {
    d3.select(this)
      .text('')
      .attr('font-weight', d.weight)
      .tspans(
        wordwrap(
          `${apMonths.get(parseInt(d.date.format('M')))} ${d.date.format(
            'YYYY'
          )}`,
          10
        )
      );
  });

  recordAnno.attr('y', frameWidth > breakPoint ? 17 : 13);

  recordAnno.each(function(d) {
    d3.select(this)
      .text('')
      .attr('font-weight', d.weight)
      .tspans(
        wordwrap(
          `${d.date.format('MMMM YYYY')} ${d.text}`,
          frameWidth > breakPoint ? 10 : 10
        )
      )
      .attr('text-anchor', d => d.anchor);
  });

  // annotate to the left (right-align it, say tx was shutdown)

  function drawEventArea(
    beginDate,
    endDate,
    labelText,
    labelTextDate,
    labelAnchor,
    yPosition
  ) {
    let labels = g.append('g').attr('class', 'area-label-elements');

    let elements = labels
      .selectAll('g')
      .data(getLabeledData(rateData, ['']))
      .enter()
      .append('g')
      .attr('class', 'area-label-element');

    elements
      .append('rect')
      .attr('class', 'column')
      .attr('fill', '#000')
      .attr('opacity', 0.09)
      .attr('x', x(moment(beginDate, 'YYYY MMM')))
      .attr('y', y(12))
      .attr(
        'width',
        x(moment(endDate, 'YYYY MMM')) - x(moment(beginDate, 'YYYY MMM'))
      )
      .attr('height', y(0) - y(12));

    let text = elements
      .append('g')
      .attr('class', 'area-text')
      .attr('text-anchor', labelAnchor);

    text
      .append('text')
      .text('')
      .tspans(wordwrap(labelText, 10));

    text.attr(
      'transform',
      'translate(' +
        (frameWidth > breakPoint ? x(labelTextDate) : x(labelTextDate)) +
        ',' +
        y(yPosition) +
        ')'
    );
  }
}

function getLabeledData(data, dates) {
  // always annotate the latest datapoint
  let labeledData = [data[data.length - 1]];

  dates.forEach(date => {
    date = moment(date, 'YYYY MMM'); // format needs to match date format in the data
    let foundData = data.find(d => d.date.isSame(date));
    if (foundData) {
      labeledData.push(foundData);
    }
  });

  return labeledData;
}

export default render;
